// src/pages/VisaActor.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import 'moment/locale/fr';
import { getPublications } from '../services/publicationService';

// Thème personnalisé (identique à la version statique)
const theme = createTheme({
  palette: {
    primary: {
      main: '#388E3C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2E7D32',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: '1.8rem',
      color: '#2E7D32',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.1rem',
      lineHeight: 1.2,
    },
    body1: {
      fontSize: '0.95rem',
      color: '#4a4a4a',
    },
    body2: {
      fontSize: '0.85rem',
      color: '#6c757d',
    },
  },
  shape: {
    borderRadius: 8,
  },
});

// Composants stylisés
const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: 250,
  backgroundColor: theme.palette.background.paper,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.light,
    borderWidth: 2,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.dark,
    borderWidth: 2,
  },
}));

const DocumentCard = styled(Card)(({ theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

function VisaActor() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [visaData, setVisaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'vs', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le filtre et l'affichage
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setVisaData(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Filtrage des documents par année
  const filteredDocs = useMemo(() => {
    if (selectedYear === 'Tous') return visaData;
    return visaData.filter(doc => doc.date.getFullYear().toString() === selectedYear);
  }, [visaData, selectedYear]);

  // Options d'années
  const yearOptions = useMemo(() => {
    const years = [...new Set(visaData.map(doc => doc.date.getFullYear()))];
    return ['Tous', ...years.sort((a, b) => b - a)];
  }, [visaData]);

  const handleShowMore = () => {
    setVisibleCount(prev => prev + 4);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setVisibleCount(4);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <NavigationBar />
          <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              Visa Statistiques Octroyés
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <NavigationBar />
          <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              Visa Statistiques Octroyés
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavigationBar />

        <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Visa Statistiques Octroyés
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            Consultez les visas statistiques officiels accordés par l'Autorité Statistique Nationale.
            Ces documents valident les méthodologies et protocoles des enquêtes menées au Burundi.
          </Typography>

          {/* Filtres */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <FormControl variant="outlined" sx={{ minWidth: 250 }}>
              <InputLabel id="year-filter-label">Année de publication</InputLabel>
              <StyledSelect
                labelId="year-filter-label"
                value={selectedYear}
                onChange={handleYearChange}
                label="Année de publication"
              >
                {yearOptions.map(year => (
                  <MenuItem key={year} value={year.toString()}>
                    {year === 'Tous' ? 'Toutes les années' : year}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>

          {/* Résultats */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {filteredDocs.length} résultat(s) trouvé(s)
          </Typography>

          {/* Liste des documents */}
          <Grid container spacing={1}>
            {filteredDocs.slice(0, visibleCount).map((doc, index) => (
              <Grid item xs={12} key={index}>
                <DocumentCard elevation={2}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    height: '100%'
                  }}>
                    <a
                      href={doc.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'inherit',
                        textDecoration: 'none',
                      }}
                    >
                      <CardMedia
                        component="img"
                        sx={{
                          width: { xs: '100%', sm: 180 },
                          height: { xs: 200, sm: 'auto' },
                          objectFit: 'cover'
                        }}
                        image={doc.image}
                        alt={doc.title}
                      />
                    </a>

                    <Box sx={{
                      p: 3,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        <a
                          href={doc.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          <DownloadIcon /> {doc.title}
                        </a>
                      </Typography>
                      <Typography variant="body1" paragraph sx={{ flex: 1 }}>
                        {doc.description}
                      </Typography>

                      <Typography variant="caption" color="textSecondary">
                        Publié le {moment(doc.date).locale('fr').format('LL')}
                      </Typography>
                    </Box>
                  </Box>
                </DocumentCard>
              </Grid>
            ))}
          </Grid>

          {/* Bouton "Voir plus" */}
          {visibleCount < filteredDocs.length && (
            <Box sx={{ textAlign: 'justify', mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleShowMore}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Afficher plus de visas
              </Button>
            </Box>
          )}
        </Container>

        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default VisaActor;